<section class="card my-3" *ngIf="status?.visible" aria-labelledby="credential-transition-title">
  <div class="card-body">
    <h2 id="credential-transition-title" class="h4">{{status.transitionID ? status.processName : 'Credential Transition'}}</h2>
    <div *ngIf="!status.transitionID">
      <p>Select a credential transition, review its requirements, and begin. You can have only one active transition.</p>
      <label for="ct-choice">Available process</label>
      <select id="ct-choice" class="form-control mb-3" [(ngModel)]="selectedVersionID" [disabled]="busy">
        <option [ngValue]="null">Select a process</option>
        <option *ngFor="let option of status.options" [ngValue]="option.processVersionID">{{option.name}} — {{option.targetCredential}}</option>
      </select>
      <div *ngIf="selectedOption">
        <p>{{selectedOption.description}}</p>
        <ul>
          <li>Target credential verification</li>
          <li *ngIf="selectedOption.requireExamDate">Exam date</li>
          <li *ngIf="selectedOption.requireCredentialUpload">Credential upload</li>
          <li *ngIf="selectedOption.requireKra">KRA Self and Oversight Reviews, each submitted within {{selectedOption.kraValidityDays}} calendar days, with any required oversight approval</li>
        </ul>
        <button class="btn btn-primary mb-3" [disabled]="busy" (click)="begin()">Begin</button>
      </div>
    </div>
    <p *ngIf="status.transitionID && status.requireExamDate">Enter your scheduled exam date<span *ngIf="status.requireKra"> to open your required reviews</span>. If you reschedule or do not pass, update the date here. Your transition stays open until your offer is completed.</p>
    <p class="alert alert-info" *ngIf="status.paused">This process is paused. You may complete requirements; an offer cannot be issued until the process resumes.</p>
    <fieldset *ngIf="status.transitionID && status.requireExamDate" [disabled]="busy || !status.canEdit">
      <div class="form-group">
        <label for="rbt-exam-date">Exam date</label>
        <div class="d-flex flex-wrap align-items-center">
          <input
            id="rbt-exam-date"
            type="date"
            class="form-control w-auto mr-2 mb-2"
            [(ngModel)]="examDate"
            min="2000-01-01"
            max="2099-12-31"
          />
          <button type="button" class="btn btn-primary mb-2" (click)="saveDate()" [disabled]="!examDate">Save exam date</button>
        </div>
      </div>
    </fieldset>
    <dl class="row" *ngIf="status.transitionID">
      <dt class="col-sm-5" *ngIf="status.requireKra">KRA Self Review</dt>
      <dd class="col-sm-7" *ngIf="status.requireKra">{{ status.selfReviewStatus }} <span *ngIf="status.selfReviewExpiresOn">— valid through {{status.selfReviewExpiresOn | date:'MM/dd/yyyy'}}</span> <a
          *ngIf="status.selfReviewID && !status.selfComplete && status.canEdit"
          [routerLink]="['/employee-review/player', status.selfReviewID]"
        >Open self review</a></dd>
      <dt class="col-sm-5" *ngIf="status.requireKra">KRA Oversight Review</dt>
      <dd class="col-sm-7" *ngIf="status.requireKra">{{ status.oversightReviewStatus }} <span *ngIf="status.oversightReviewExpiresOn">— valid through {{status.oversightReviewExpiresOn | date:'MM/dd/yyyy'}}</span> <a
          *ngIf="status.oversightReviewID && status.oversightReviewExpiresOn"
          [routerLink]="['/employee-review/oversight/view', status.oversightReviewID]"
        >View oversight review</a></dd>
      <dt class="col-sm-5" *ngIf="status.requireKra">KRA valid through</dt>
      <dd class="col-sm-7" *ngIf="status.requireKra">{{ status.kraExpiresOn ? (status.kraExpiresOn | date:'MM/dd/yyyy') : 'Available once both reviews are submitted' }} <span *ngIf="status.kraExpired">— Expired</span><span *ngIf="status.oversightReviewStatus === 'Submitted — awaiting approval'"> — oversight approval required before an offer can be sent</span></dd>
      <dt class="col-sm-5">{{status.targetAbbreviation || status.targetCredential}} credential</dt>
      <dd class="col-sm-7">{{ status.credentialStatus }}</dd>
      <dt class="col-sm-5">Offer/change</dt>
      <dd class="col-sm-7">{{ status.offerStatus }} <a *ngIf="status.canOpenOffer" routerLink="/offerletter">Open offer</a></dd>
      <dt class="col-sm-5" *ngIf="status.hourlyRate">Hourly rate</dt>
      <dd class="col-sm-7" *ngIf="status.hourlyRate">{{ status.hourlyRate | currency }} / hour<span *ngIf="status.trainingRate != null">; training {{ status.trainingRate | currency }} / hour</span></dd>
    </dl>
    <p class="alert alert-info" *ngIf="status.message">{{ status.message }}</p>
    <button
      class="btn btn-warning mb-3"
      *ngIf="status.payDecreaseWarning && !status.paused"
      [disabled]="busy"
      (click)="confirmPayDecrease()"
    >Review pay decrease warning</button>
    <div class="d-flex flex-wrap">
      <button
        type="button"
        class="btn btn-outline-primary mr-2 mb-2"
        *ngIf="status.transitionID && status.canEdit"
        [disabled]="busy || !status.canUploadCredential"
        (click)="showUpload = !showUpload"
      >{{ status.credentialStatus === 'Not Uploaded' ? 'Upload credential' : 'Upload replacement credential' }}</button>
      <button type="button" class="btn btn-outline-secondary mb-2" [disabled]="busy" (click)="refresh()">Refresh status</button>
    </div>
    <p *ngIf="status.canEdit && !status.canUploadCredential">Credential replacement is unavailable while an offer exists. Complete the offer or contact HR if it needs to be withdrawn.</p>
    <fieldset *ngIf="showUpload && status.canUploadCredential" [disabled]="busy" class="border rounded p-3 mt-2">
      <legend class="h5">{{status.targetAbbreviation || status.targetCredential}} credential/certificate</legend>
      <p>Your upload goes to the existing credentialing queue for verification for this process. Enter the dates printed on your credential; do not guess missing information.</p>
      <div class="form-group">
        <label for="ct-credential-type">Credential type</label>
        <select
          id="ct-credential-type"
          class="form-control"
          [(ngModel)]="license.isBoardCertification"
          (ngModelChange)="license.transitionUploadToken = null"
        >
          <option [ngValue]="null">Select the credential type</option>
          <option value="false">License/certificate</option>
          <option value="true">Board certification</option>
        </select>
      </div>
      <div class="form-group">
        <label for="rbt-number">{{status.targetAbbreviation || status.targetCredential}} credential number</label>
        <input
          id="rbt-number"
          class="form-control"
          [(ngModel)]="license.licenseNumber"
          (ngModelChange)="license.transitionUploadToken = null"
          maxlength="255"
        />
      </div>
      <div class="row">
        <div class="form-group col-md-4">
          <label for="rbt-origin">Original certification date</label>
          <input
            id="rbt-origin"
            type="date"
            class="form-control"
            [(ngModel)]="license.originationDate"
            (ngModelChange)="license.transitionUploadToken = null"
          />
        </div>
        <div class="form-group col-md-4">
          <label for="rbt-issued">Issue date</label>
          <input
            id="rbt-issued"
            type="date"
            class="form-control"
            [(ngModel)]="license.issueDate"
            (ngModelChange)="license.transitionUploadToken = null"
          />
        </div>
        <div class="form-group col-md-4">
          <label for="rbt-expires">Expiration date (if shown)</label>
          <input
            id="rbt-expires"
            type="date"
            class="form-control"
            [(ngModel)]="license.expirationDate"
            (ngModelChange)="license.transitionUploadToken = null"
          />
        </div>
      </div>
      <div class="form-group">
        <label for="rbt-file">Credential file</label>
        <input
          id="rbt-file"
          type="file"
          class="form-control-file"
          accept="application/pdf,image/*"
          (change)="chooseFile($event)"
        />
      </div>
      <div class="form-check mb-3">
        <input id="rbt-confirm" type="checkbox" class="form-check-input" [(ngModel)]="confirmedRbt" />
        <label for="rbt-confirm" class="form-check-label">This is my {{status.targetAbbreviation || status.targetCredential}} credential.</label>
      </div>
      <button
        type="button"
        class="btn btn-primary"
        (click)="upload()"
        [disabled]="!confirmedRbt || license.isBoardCertification == null || !file || !license.licenseNumber || !license.originationDate || !license.issueDate"
      >Submit for verification</button>
    </fieldset>
    <details class="mt-3" *ngIf="status.transitionID">
      <summary>How this transition works</summary>
      <p class="mt-2">Your target credential must be verified before an offer is generated. <span *ngIf="status.requireKra">Each self and oversight submission is valid for {{status.kraValidityDays}} calendar days, including its submission date. Matching recent submissions can be reused when this process allows it. Changing the exam date does not require new reviews while they remain valid; only a missing or expired review needs replacement. Required oversight approval must be completed before an offer is sent and does not extend either review's validity.</span> Credential upload does not depend on KRA completion. Job titles and pay change only after the offer is fully signed. Your source credential is retained.</p>
      <p *ngIf="!status.requireCredentialUpload">An upload is optional. An authorized credentialing user must explicitly verify the target credential on your employee record for this process.</p>
    </details>
  </div>
</section>
<div class="alert alert-success my-3" *ngIf="status?.showSignaturePrompt">
  <h2 class="h5">Your {{status.processName}} is complete</h2>
  <p>Please update your professional signature/name-and-credentials to include {{status.targetAbbreviation || status.targetCredential}}. Your drawn signature and professional name are not changed automatically.</p>
  <button type="button" class="btn btn-success" [disabled]="busy" (click)="acknowledge()">I have updated my professional signature</button>
</div>
<p class="alert alert-danger" role="alert" *ngIf="error">{{ error }}</p>
<p role="status" *ngIf="busy">Saving or refreshing your credential transition…</p>
